/**
 * The tokens a note's *body* renders, once media has been lifted out of it.
 *
 * Media is hoisted into the gallery, so an image token renders as nothing — but
 * the whitespace around it was still the author's text and still rendered, under
 * `whitespace-pre-wrap`, as the blank lines they typed. A note written the ordinary
 * way,
 *
 *     https://example.com/shot.png
 *
 *     @someone should we collab?
 *
 * therefore opened with a space and two line breaks that separated the text from an
 * image no longer above it, and the row began with two empty lines. It reads as a
 * broken note, and it is the commonest shape there is: a screenshot and a sentence.
 *
 * So whitespace that only exists to separate something no longer present is
 * dropped. Whitespace the author put between two things that *are* both still
 * rendered is left exactly as written — a note is not reflowed here, and the blank
 * lines someone typed between two paragraphs are theirs.
 */

import type { ContentToken } from "@setu/protocol";

/** Tokens the gallery renders, so the body must not. */
function isHoisted(token: ContentToken): boolean {
  return token.type === "image" || token.type === "video";
}

/** A token that contributes only space to the rendered body. */
function isBlank(token: ContentToken): boolean {
  if (token.type === "newline") return true;
  return token.type === "text" && token.value.trim() === "";
}

/**
 * Drops hoisted media and the whitespace that only separated it.
 *
 * A run of blank tokens containing hoisted media collapses: to nothing at the
 * start or end of the note, and to a single paragraph break in the middle — where
 * media sat *between* two paragraphs, the reader still needs them separated, but by
 * one gap rather than the author's gap twice over.
 */
export function bodyTokens(
  tokens: readonly ContentToken[],
): readonly ContentToken[] {
  const out: ContentToken[] = [];
  let index = 0;

  while (index < tokens.length) {
    const token = tokens[index] as ContentToken;
    if (!isBlank(token) && !isHoisted(token)) {
      out.push(token);
      index += 1;
      continue;
    }

    // The whole run of blanks and hoisted media, taken together: the decision
    // needs to know whether anything was removed from inside it.
    const blanks: ContentToken[] = [];
    let removed = false;
    while (index < tokens.length) {
      const next = tokens[index] as ContentToken;
      if (isHoisted(next)) {
        removed = true;
        index += 1;
        continue;
      }
      if (isBlank(next)) {
        blanks.push(next);
        index += 1;
        continue;
      }
      break;
    }

    // Nothing was lifted out, so this is the author's own spacing between two
    // things the reader can see. Untouched.
    if (!removed) {
      out.push(...blanks);
      continue;
    }
    // Leading or trailing: there is nothing left for it to separate.
    if (out.length === 0 || index >= tokens.length) continue;
    /*
     * Only line breaks are collapsed, and only down to a paragraph.
     *
     * Spaces are deliberately left alone. A URL inside a sentence has the author's
     * own spaces on either side of it, and those spaces belong to the *text* tokens
     * around the removed one — closing the gap would mean editing their words, for
     * a doubled space in the middle of a line that nobody reads as broken. Blank
     * lines are the case worth fixing: they push the note's first sentence off the
     * top of the row.
     */
    out.push(...blanks.filter((blank) => blank.type === "newline").slice(0, 2));
  }

  return out;
}
